import React from 'react';
import { Toast } from '@core/uilibrary';
import "../css/ToastProvider.css"
import {
  SuccessToastIcon,
  ErrorToastIcon,
} from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter';

export type ToastStatus = 'info' | 'success' | 'warning' | 'error';

export type ToastItem = {
  id: string;
  status: ToastStatus;
  title: string;
  description?: string;
  duration?: number;
  isExiting?: boolean;
};

type ToastContextValue = {
  showToast: (toast: Omit<ToastItem, 'id'>) => string;
  dismissToast: (id: string) => void;
};

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

type ToastProviderProps = {
  children: React.ReactNode;
  placement?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  defaultDuration?: number;
};

/* ── Status icon map ─────────────────────────────────────── */

const InfoToastIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#D6EAFF"/>
    <path d="M16 6C10.477 6 6 10.477 6 16C6 21.523 10.477 26 16 26C21.523 26 26 21.523 26 16C26 10.477 21.523 6 16 6ZM16 11C16.552 11 17 11.448 17 12C17 12.552 16.552 13 16 13C15.448 13 15 12.552 15 12C15 11.448 15.448 11 16 11ZM17.5 21H14.5C13.948 21 13.5 20.552 13.5 20C13.5 19.448 13.948 19 14.5 19H15V16H14.5C13.948 16 13.5 15.552 13.5 15C13.5 14.448 13.948 14 14.5 14H16C16.552 14 17 14.448 17 15V19H17.5C18.052 19 18.5 19.448 18.5 20C18.5 20.552 18.052 21 17.5 21Z" fill="#2388FF"/>
  </svg>
);

const WarningToastIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="16" fill="#FFF3D6"/>
    <path d="M25.544 21.634L18.036 8.358C17.752 7.842 17.201 7.513 16.6 7.5H15.4C14.799 7.513 14.248 7.842 13.964 8.358L6.456 21.634C6.159 22.17 6.164 22.824 6.469 23.355C6.774 23.886 7.337 24.21 7.946 24.21H24.054C24.663 24.21 25.226 23.886 25.531 23.355C25.836 22.824 25.841 22.17 25.544 21.634ZM15 13C15 12.448 15.448 12 16 12C16.552 12 17 12.448 17 13V18C17 18.552 16.552 19 16 19C15.448 19 15 18.552 15 18V13ZM16 22.5C15.31 22.5 14.75 21.94 14.75 21.25C14.75 20.56 15.31 20 16 20C16.69 20 17.25 20.56 17.25 21.25C17.25 21.94 16.69 22.5 16 22.5Z" fill="#FF9F0A"/>
  </svg>
);

const TOAST_ICONS: Record<ToastStatus, React.ReactNode> = {
  success: <SuccessToastIcon />,
  error:   <ErrorToastIcon />,
  info:    <InfoToastIcon />,
  warning: <WarningToastIcon />,
};

/* ── Provider ────────────────────────────────────────────── */

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  placement = 'bottom-left',
  defaultDuration = 5000,
}) => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const hideTimers   = React.useRef<Record<string, number>>({});
  const removeTimers = React.useRef<Record<string, number>>({});

  const MAX_TOASTS = 5;

  const showToast = React.useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`; //NOSONAR

    setToasts((prev) => {
      // Remove oldest toasts (last in array) if at limit
      const trimmed = prev.length >= MAX_TOASTS ?  /* istanbul ignore next */ prev.slice(0, MAX_TOASTS - 1) : prev;
      return [{ id, ...toast }, ...trimmed];
    });

    const duration = toast.duration ?? defaultDuration;
    /* istanbul ignore next */
    if (typeof globalThis.window !== 'undefined') { //NOSONAR
      hideTimers.current[id] = globalThis.window.setTimeout(() => {
        dismissToast(id);
      }, duration);
    }
    return id;
  }, [defaultDuration]);

  const dismissToast = React.useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, isExiting: true } : /* istanbul ignore next */ t))
    );
    /* istanbul ignore next */
    if (typeof globalThis.window !== 'undefined') { //NOSONAR
      removeTimers.current[id] = globalThis.window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id)); //NOSONAR
        delete removeTimers.current[id];
      }, 300);
    }
    /* istanbul ignore next */
    if (typeof globalThis.window !== 'undefined') { //NOSONAR
      /* istanbul ignore next */
      if (hideTimers.current[id]) {
        globalThis.window.clearTimeout(hideTimers.current[id]);
        delete hideTimers.current[id];
      }
    }
  }, []);

  React.useEffect(() => () => {
    /* istanbul ignore next */
    if (typeof globalThis.window === 'undefined') /* istanbul ignore next */ return; //NOSONAR
    Object.values(hideTimers.current).forEach((t) => globalThis.window.clearTimeout(t));   //NOSONAR
    Object.values(removeTimers.current).forEach((t) => globalThis.window.clearTimeout(t)); //NOSONAR
  }, []);

  const placementClass = React.useMemo(() => {
    switch (placement) {
      case 'bottom-right': return 'scl--toastProvider__stack--br';
      case 'top-left':     return 'scl--toastProvider__stack--tl';
      case 'top-right':    return 'scl--toastProvider__stack--tr';
      default:             return 'scl--toastProvider__stack--bl';
    }
  }, [placement]);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}> {/* NOSONAR */}
      {children}
      <div className={`scl--toastProvider__stack ${placementClass}`}>
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`scl--toastProvider__item ${t.isExiting ? 'tp-exit' : ''}`}
          >
            {/* @ts-ignore: package path provided in app */}
            <Toast
              visible
              icon={TOAST_ICONS[t.status]}
              status={t.status}
              title={t.title}
              description={t.description ?? ''}
              onClose={() => dismissToast(t.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};